import { useForm, useFieldArray, useWatch, Controller } from 'react-hook-form';
import { forwardRef, useImperativeHandle, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2 } from 'lucide-react';
import {
  OrgTableRow,
  newTempKey,
  getParentOptions,
  validateOrgTableRows,
} from '@/pages/company-profile/organization/org-structure-table';
import { ORG_DIVISIONS, ORG_LEVELS } from '@/utils/constants/organization-matrix';

export interface OrgStructureTableRef {
  save: () => Promise<void>;
}

interface OrgStructureTableProps {
  initialRows: OrgTableRow[];
  onSave: (rows: OrgTableRow[]) => Promise<void>;
  onChange?: (rows: OrgTableRow[]) => void;
  disabled?: boolean;
  jabatanOptions?: string[];
  jabatanOptionsLoading?: boolean;
}

interface FormValues {
  rows: OrgTableRow[];
}

const newRootRow = (): OrgTableRow => ({
  tempKey: newTempKey(),
  name: '',
  division_key: '',
  level_key: '',
  description: '',
  parentTempKey: null,
});

const OrgStructureTable = forwardRef<OrgStructureTableRef, OrgStructureTableProps>(function OrgStructureTable(
  { initialRows, onSave, onChange, disabled = false, jabatanOptions, jabatanOptionsLoading },
  ref,
) {
  const form = useForm<FormValues>({
    defaultValues: { rows: initialRows.length ? initialRows : [newRootRow()] },
  });

  const { control, getValues, setValue } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'rows' });
  const watchedRows = useWatch({ control, name: 'rows' }) ?? [];

  useEffect(() => {
    if (onChange) onChange(watchedRows);
  }, [watchedRows, onChange]);

  const handleRemove = (index: number) => {
    const rows = getValues('rows');
    const removedKey = rows[index]?.tempKey;
    if (removedKey) {
      rows.forEach((r, i) => {
        if (r.parentTempKey === removedKey) {
          setValue(`rows.${i}.parentTempKey`, null);
        }
      });
    }
    remove(index);
  };

  const handleSave = async () => {
    const rows = getValues('rows');
    const err = validateOrgTableRows(rows);
    if (err) {
      toast.error(err);
      return;
    }
    await onSave(rows);
  };

  useImperativeHandle(ref, () => ({ save: handleSave }));

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[720px] w-full text-sm border-collapse border border-gray-200 dark:border-gray-700">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center w-12">No</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">Jabatan</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">Divisi</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">Level</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">Atasan</th>
            <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center w-28">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, index) => {
            const rowTempKey = watchedRows[index]?.tempKey ?? field.tempKey;
            const parentOptions = getParentOptions(
              watchedRows.length
                ? watchedRows
                : fields.map(f => ({
                    tempKey: f.tempKey,
                    name: f.name,
                    division_key: f.division_key,
                    level_key: f.level_key,
                    description: f.description,
                    parentTempKey: f.parentTempKey,
                  })),
              rowTempKey,
            );

            return (
              <tr
                key={field.id}
                className="border-b border-gray-100 dark:border-gray-700 align-middle hover:bg-gray-50/50 dark:hover:bg-gray-800/50"
              >
                {/* No */}
                <td className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 text-center">{index + 1}</td>

                {/* Jabatan */}
                <td className="px-2 py-2">
                  <Controller
                    control={control}
                    name={`rows.${index}.name`}
                    render={({ field: controllerField }) => (
                      <input
                        type="text"
                        value={controllerField.value ?? ''}
                        onChange={e => controllerField.onChange(e.target.value)}
                        aria-label={`Jabatan ${index}`}
                        disabled={disabled}
                        placeholder="Ketik nama jabatan..."
                        className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                      />
                    )}
                  />
                </td>

                {/* Divisi */}
                <td className="px-2 py-2">
                  <Controller
                    control={control}
                    name={`rows.${index}.division_key`}
                    render={({ field: controllerField }) => (
                      <select
                        value={controllerField.value}
                        onChange={e => controllerField.onChange(e.target.value)}
                        aria-label={`Divisi ${index}`}
                        disabled={disabled}
                        className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                      >
                        <option value="" disabled>
                          Pilih divisi
                        </option>
                        {ORG_DIVISIONS.map(d => (
                          <option key={d.key} value={d.key}>
                            {d.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </td>

                {/* Level */}
                <td className="px-2 py-2">
                  <Controller
                    control={control}
                    name={`rows.${index}.level_key`}
                    render={({ field: controllerField }) => (
                      <select
                        value={controllerField.value}
                        onChange={e => controllerField.onChange(e.target.value)}
                        aria-label={`Level ${index}`}
                        disabled={disabled}
                        className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                      >
                        <option value="" disabled>
                          Pilih level
                        </option>
                        {ORG_LEVELS.map(l => (
                          <option key={l.key} value={l.key}>
                            {l.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </td>

                {/* Atasan */}
                <td className="px-2 py-2">
                  <Controller
                    control={control}
                    name={`rows.${index}.parentTempKey`}
                    render={({ field: controllerField }) => (
                      <select
                        value={controllerField.value ?? ''}
                        onChange={e => controllerField.onChange(e.target.value || null)}
                        aria-label={`Atasan ${index}`}
                        disabled={disabled}
                        className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400"
                      >
                        <option value="">— Tanpa atasan —</option>
                        {parentOptions.map(o => (
                          <option key={o.tempKey} value={o.tempKey}>
                            {o.name || '(tanpa nama)'}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </td>

                {/* Aksi */}
                <td className="px-1 py-2 text-center">
                  <div className="flex items-center gap-1 justify-center">
                    {fields.length > 1 && !disabled && (
                      <button
                        type="button"
                        aria-label={`Hapus baris ${index}`}
                        onClick={() => handleRemove(index)}
                        className="p-1 rounded-full text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex items-center justify-start gap-3 mt-4">
        {!disabled && (
          <button
            type="button"
            aria-label="Tambah jabatan"
            onClick={() =>
              append({
                tempKey: newTempKey(),
                name: '',
                division_key: '',
                level_key: '',
                description: '',
                parentTempKey: null,
              })
            }
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Tambah jabatan
          </button>
        )}
      </div>
    </div>
  );
});

export default OrgStructureTable;
